import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {RealTimeCountService} from '../../service/api/real-time-count.service';
import {RealTimeCountResponse} from '../../model/dto/api/real-time-count.interface';
import {SelectionCriteriaTree} from '../../model/dto/api/selection-criteria-tree.interface';
import {RealTimeCountStatusEnum} from '../../enum/real-time-count-status.enum';

/**
 * Real-time member count display component.
 * Implements F-025: Real-Time Member Count Updates.
 *
 * Shows live updating of estimated member counts as selection criteria
 * are being built, with delta indicators showing the impact of changes.
 */
@Component({
    selector: 'ct-real-time-count',
    standalone: false,
    template: `
        <div class="real-time-count-container flex items-center gap-2 p-2 bg-surface-50 rounded-lg border border-surface-200">
            <!-- Count Display -->
            <div class="count-display flex flex-col items-end">
                <div class="flex items-center gap-2">
                    <!-- Loading Spinner -->
                    <i *ngIf="loading" class="fa-solid fa-spinner fa-spin text-primary-500"></i>

                    <!-- Count Value -->
                    <span class="text-2xl font-bold" [class]="getCountClass()">
                        {{ formatCount() }}
                    </span>

                    <!-- Delta Indicator -->
                    <div *ngIf="showDelta && currentCount?.countDelta !== null && currentCount?.countDelta !== undefined"
                         class="delta-indicator flex items-center gap-1 text-sm"
                         [class]="getDeltaClass()">
                        <i [class]="getDeltaIcon()"></i>
                        <span>{{ formatDelta() }}</span>
                    </div>
                </div>

                <!-- Label -->
                <span class="text-xs text-surface-500">
                    {{ label }}
                </span>
            </div>

            <!-- Status & Performance Indicators -->
            <div *ngIf="showDetails" class="indicators flex flex-col items-start text-xs text-surface-400 ml-2 border-l border-surface-200 pl-2">
                <!-- Cached Indicator -->
                <span *ngIf="currentCount?.isCached" class="flex items-center gap-1">
                    <i class="fa-solid fa-database"></i>
                    Zwischengespeichert
                </span>

                <!-- Performance Indicator -->
                <span *ngIf="currentCount?.executionTimeMs" class="flex items-center gap-1" [class]="getPerformanceClass()">
                    <i class="fa-solid fa-clock"></i>
                    {{ currentCount.executionTimeMs }}ms
                </span>

                <!-- Estimation Indicator -->
                <span *ngIf="currentCount?.isEstimation" class="flex items-center gap-1 text-yellow-600">
                    <i class="fa-solid fa-chart-bar"></i>
                    Geschätzt
                </span>
            </div>

            <!-- Refresh Button -->
            <button *ngIf="showRefreshButton"
                    (click)="onRefresh()"
                    [disabled]="loading"
                    class="refresh-button p-1 text-surface-400 hover:text-primary-500 transition-colors"
                    title="Anzahl aktualisieren">
                <i class="fa-solid fa-refresh" [class.fa-spin]="loading"></i>
            </button>
        </div>
    `,
    styles: [`
        .real-time-count-container {
            min-width: 120px;
        }

        .count-display {
            min-width: 80px;
        }

        .delta-indicator {
            padding: 2px 6px;
            border-radius: 4px;
            background: rgba(0, 0, 0, 0.05);
        }

        .refresh-button:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    `]
})
export class RealTimeCountComponent implements OnInit, OnDestroy, OnChanges {

    @Input() criteriaId: number;
    @Input() criteriaTree: SelectionCriteriaTree;
    @Input() label: string = 'Geschätzte Mitglieder';
    @Input() showDelta: boolean = true;
    @Input() showDetails: boolean = true;
    @Input() showRefreshButton: boolean = true;
    @Input() autoUpdate: boolean = true;

    loading: boolean = false;
    currentCount: RealTimeCountResponse | null = null;

    private subscriptions: Subscription[] = [];

    constructor(private realTimeCountService: RealTimeCountService) {}

    ngOnInit(): void {
        this.subscriptions.push(
            this.realTimeCountService.currentCount$.subscribe(count => {
                if (count && count.selectionCriteriaId === this.criteriaId) {
                    this.currentCount = count;
                }
            }),
            this.realTimeCountService.loading$.subscribe(loading => {
                this.loading = loading;
            })
        );

        if (this.criteriaId && this.autoUpdate) {
            this.loadInitialCount();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['criteriaTree'] && !changes['criteriaTree'].firstChange) {
            if (this.autoUpdate && this.criteriaId) {
                this.realTimeCountService.onCriteriaChange(
                    this.criteriaId,
                    this.criteriaTree,
                    'CRITERIA_CHANGE'
                );
            }
        }

        if (changes['criteriaId'] && !changes['criteriaId'].firstChange) {
            this.loadInitialCount();
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];

        // Cancel pending estimates when component is destroyed
        if (this.criteriaId) {
            this.realTimeCountService.cancelPendingEstimates(this.criteriaId).subscribe();
        }
    }

    loadInitialCount(): void {
        if (!this.criteriaId) return;

        this.loading = true;
        this.realTimeCountService.getLatestEstimate(this.criteriaId).subscribe({
            next: (response) => {
                this.currentCount = response;
                this.loading = false;
            },
            error: () => {
                this.loading = false;
                this.triggerFreshCount();
            }
        });
    }

    onRefresh(): void {
        this.triggerFreshCount();
    }

    private triggerFreshCount(): void {
        if (!this.criteriaId) return;

        this.realTimeCountService.triggerImmediateUpdate({
            selectionCriteriaId: this.criteriaId,
            criteriaTree: this.criteriaTree,
            useCache: false,
            triggerType: 'MANUAL_REFRESH'
        });
    }

    formatCount(): string {
        if (this.loading && !this.currentCount) {
            return '...';
        }
        return this.realTimeCountService.formatCount(this.currentCount?.estimatedCount);
    }

    formatDelta(): string {
        return this.realTimeCountService.formatDelta(this.currentCount?.countDelta);
    }

    getCountClass(): string {
        if (!this.currentCount) {
            return 'text-surface-400';
        }
        if (this.currentCount.status === RealTimeCountStatusEnum.failed) {
            return 'text-red-500';
        }
        if (this.currentCount.estimatedCount === 0) {
            return 'text-yellow-500';
        }
        return 'text-surface-900';
    }

    getDeltaClass(): string {
        return this.realTimeCountService.getDeltaClass(this.currentCount?.countDelta);
    }

    getDeltaIcon(): string {
        return this.realTimeCountService.getDeltaIcon(this.currentCount?.countDelta);
    }

    getPerformanceClass(): string {
        return this.realTimeCountService.getPerformanceClass(this.currentCount?.executionTimeMs);
    }
}
